import { memo, useCallback, useRef, useState } from 'react';
import { Boldik, cn } from '@divlab/divanui';
import { CSSTransition } from 'react-transition-group';

import Button from '@UI/Button';
import styles from './Tab.module.css';

import type { FC, HTMLAttributes } from 'react';

export interface TabData {
  id: string;
  label: string;
  tooltip: string[];
}

interface TabProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  item: TabData;
  onChangeTab?: (tab: TabData) => void;
  idSelectedTab?: string;
  tooltip: string[];
  isHiddenTooltip?: boolean;
}

interface CoordsData {
  left: string;
}

const Tab: FC<TabProps> = (props) => {
  const { item, onChangeTab, idSelectedTab, tooltip, isHiddenTooltip, ...restProps } = props;
  const [opened, setOpened] = useState<string>(null);
  const [coords, setCoords] = useState<CoordsData>(null);
  const refTooltip = useRef<HTMLDivElement>(null);
  const refButton = useRef<HTMLButtonElement>(null);

  const setPosition = useCallback(() => {
    if (!refTooltip.current) return;
    if (!refButton.current) return;

    const { clientWidth } = window.document.documentElement;
    const boxTooltip = refTooltip.current.getBoundingClientRect();
    const boxButton = refButton.current.getBoundingClientRect();

    if (boxTooltip.left < 100) {
      setCoords({ left: '0px' });
    } else if (boxTooltip.right > clientWidth - 100) {
      setCoords({ left: `${-boxTooltip.width + boxButton.width}px` });
    } else {
      setCoords({ left: `${-boxTooltip.width / 2 + boxButton.width / 2}px` });
    }
  }, []);

  const handleMouseEnter = () => {
    setOpened(item.id);
    setTimeout(setPosition, 100);
  };

  const handleMouseLeave = () => {
    setOpened(null);
  };

  const handleChangeTab = () => {
    if (onChangeTab) onChangeTab(item);
  };

  return (
    <div {...restProps} className={styles.buttonWrapper}>
      <Button
        className={cn(styles.button, {
          [styles.activeButton]: item.id === idSelectedTab,
        })}
        view='rounded'
        theme={item.id === idSelectedTab ? 'dirty' : 'transparent'}
        onClick={handleChangeTab}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={refButton}
      >
        <Boldik>{item.label}</Boldik>
      </Button>
      <CSSTransition
        classNames={{
          enter: styles.enter,
          enterActive: styles.enterActive,
          enterDone: styles.enterDone,
          exit: styles.exit,
          exitActive: styles.exitActive,
          exitDone: styles.exitDone,
        }}
        in={opened === item.id && !isHiddenTooltip}
        unmountOnExit
        timeout={400}
      >
        <div className={cn(styles.tooltip)} ref={refTooltip} style={coords}>
          <div>
            {tooltip.length > 1 ? (
              tooltip.map((text, index) => <div key={index}>- {text}</div>)
            ) : (
              <div>{tooltip[0]}</div>
            )}
          </div>
        </div>
      </CSSTransition>
    </div>
  );
};

export default memo(Tab);
